import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import ReactConfetti from 'react-confetti'
import { useWindowSize } from 'react-use'
import { CONFETTI_BURST_EVENT } from './confettiBurstEvent'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'

type ConfettiComponentProps = React.ComponentProps<typeof ReactConfetti>

type ConfettiBurstOverlayProps = {
    className?: string
    options?: Partial<ConfettiComponentProps>
}

export default function ConfettiBurstOverlay({
    className,
    options,
}: ConfettiBurstOverlayProps) {
    const { width, height } = useWindowSize()
    const [burstKey, setBurstKey] = useState(0)
    const [isActive, setIsActive] = useState(false)
    const [isFadingOut, setIsFadingOut] = useState(false)
    const [eventOptions, setEventOptions] = useState<Partial<ConfettiComponentProps> | null>(null)
    const stopTimeoutRef = useRef<number | null>(null)
    const fadeTimeoutRef = useRef<number | null>(null)

    const isStele = useSteleBreakpoint()
    const burstDuration = isStele ? 7200 : 5200
    const fadeDuration = isStele ? 1400 : 1000

    useEffect(() => {
        const triggerBurst = (event: Event) => {
            const customEvent = event as CustomEvent<Partial<ConfettiComponentProps> | undefined>

            if (stopTimeoutRef.current !== null) {
                window.clearTimeout(stopTimeoutRef.current)
            }
            if (fadeTimeoutRef.current !== null) {
                window.clearTimeout(fadeTimeoutRef.current)
            }

            setBurstKey((current) => current + 1)
            setIsActive(true)
            setIsFadingOut(false)
            setEventOptions(customEvent.detail ?? null)

            fadeTimeoutRef.current = window.setTimeout(() => {
                setIsFadingOut(true)
                fadeTimeoutRef.current = null
            }, burstDuration - fadeDuration)

            stopTimeoutRef.current = window.setTimeout(() => {
                setIsActive(false)
                setIsFadingOut(false)
                setEventOptions(null)
                stopTimeoutRef.current = null
            }, burstDuration)
        }

        window.addEventListener(CONFETTI_BURST_EVENT, triggerBurst)

        return () => {
            window.removeEventListener(CONFETTI_BURST_EVENT, triggerBurst)
            if (stopTimeoutRef.current !== null) {
                window.clearTimeout(stopTimeoutRef.current)
            }
            if (fadeTimeoutRef.current !== null) {
                window.clearTimeout(fadeTimeoutRef.current)
            }
        }
    }, [burstDuration, fadeDuration])

    if (!isActive) {
        return null
    }

    const effectScale = isStele ? 4 : 1
    const particleScale = effectScale
    const sourceSize = isStele ? Math.min(width, height) * 0.12 : 0
    const confettiSource = {
        x: width * 0.5 - sourceSize / 2,
        y: height * 0.5 - sourceSize / 2,
        w: sourceSize,
        h: sourceSize,
    }

    const drawShape = function (this: { shape?: string; radius: number; w: number; h: number }, context: CanvasRenderingContext2D) {
        if (this.shape === 'circle') {
            context.beginPath()
            context.arc(0, 0, this.radius * particleScale, 0, 2 * Math.PI)
            context.fill()
            return
        }

        context.fillRect(
            (-this.w * particleScale) / 2,
            (-this.h * particleScale) / 2,
            this.w * particleScale,
            this.h * particleScale,
        )
    }

    return (
        <div
            className={clsx('absolute inset-0 pointer-events-none', className)}
            style={{
                opacity: isFadingOut ? 0 : 1,
                transition: `opacity ${fadeDuration}ms ease-out`,
            }}
        >
            <ReactConfetti
                key={burstKey}
                width={width}
                height={height}
                recycle={false}
                numberOfPieces={isStele ? 320 : 220}
                gravity={isStele ? 0.18 : 0.12}
                initialVelocityY={18 * effectScale}
                initialVelocityX={12 * effectScale}
                tweenDuration={burstDuration}
                confettiSource={confettiSource}
                drawShape={drawShape}
                {...options}
                {...eventOptions}
            />
        </div>
    )
}
